import { provideHttpClient } from "@angular/common/http";
import { ApplicationConfig, ErrorHandler, importProvidersFrom } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideRouter, Routes, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { biTranslateProviders } from "@globals/bi-translate/bi-translate-providers";
import { SharedRouteNames } from "@globals/classes/SharedRouteNames";
import { getLanguageCodeByHostName } from "@globals/helper-functions";
import { BiErrorHandlerService } from "@globals/services/bi-error-handler.service";
import { GoogleCaptchaManagerService } from "@globals/services/google-captcha-manager.service";
import { APPLICATION_NAME } from "@globals/services/injection-tokens/application-name-token";
import { BI_ENVIRONMENT } from "@globals/services/injection-tokens/environment-token";
import { RECAPTCHA_LOADER_OPTIONS, RECAPTCHA_SETTINGS, RecaptchaSettings } from "ng-recaptcha-2";
import { ToastrModule } from "ngx-toastr";
import { ConfirmationService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import { environment } from "src/environments/environment";
import { IframeModulesPrimeNgThemePreset } from "../iframe-modules-primeNg-theme-preset";
function getPathName() {
  // Remove the leading and ending "/" if present before comparing to route name values.
  let pathName = window.location.pathname;
  if (pathName.startsWith("/")) pathName = pathName.substring(1);
  if (pathName.endsWith("/")) pathName = pathName.substring(0, pathName.length - 1);
  return pathName;
}

function loadRouteConfigFunc() {
  // Load component based on current URL's path name
  const pathName = getPathName().toLowerCase();
  switch (pathName) {
    case SharedRouteNames.iFrameRoutes.driftstatus.toLowerCase():
      return import("./driftstatus/iframe-driftstatus.routes");
    case SharedRouteNames.iFrameRoutes.driftstatusMap.toLowerCase():
      return import("./driftstatus-map/iframe-driftstatus-map.routes");
    case SharedRouteNames.iFrameRoutes.subscription.toLowerCase():
      return import("./subscribe-unsubscribe/subscription-module-normal/iframe-subscription.routes");
    case SharedRouteNames.iFrameRoutes.stdReceivers.toLowerCase():
      return import("./subscribe-unsubscribe/subscription-module-std-receivers/iframe-subscription-std-receiver.routes");
  }
}

const routes: Routes = [
  { path: SharedRouteNames.unavailable, loadComponent: () => import("@globals/components/unavailable/unavailable.component") },
  { path: "", loadChildren: () => loadRouteConfigFunc() }
];

export const iframeModulesAppConfig: ApplicationConfig = {
  providers: [
    { provide: BI_ENVIRONMENT, useValue: environment },
    { provide: APPLICATION_NAME, useValue: "Iframe Modules App" },
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always"
      }),
      withInMemoryScrolling({ scrollPositionRestoration: "top" }),
      withComponentInputBinding()
    ),
    ...biTranslateProviders,
    provideHttpClient(),
    providePrimeNG({
      theme: {
        preset: IframeModulesPrimeNgThemePreset,
        options: {
          darkModeSelector: false || "none" // disables dark mode selector
        }
      }
    }),
    importProvidersFrom(ToastrModule.forRoot({ timeOut: 10000, closeButton: true }), BrowserAnimationsModule, BrowserModule),
    ConfirmationService,
    { provide: ErrorHandler, useClass: BiErrorHandlerService },

    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: GoogleCaptchaManagerService.googleCaptchaSiteKey } as RecaptchaSettings
    },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useValue: {
        onBeforeLoad(url: URL) {
          const languageCode = getLanguageCodeByHostName();
          url.searchParams.set("hl", languageCode === "se" ? "sv" : languageCode);
          return { url };
        }
      }
    }
  ]
};
